import React, { useState, useEffect, memo } from 'react';
import { Icons } from '../Icons';
import { getMetricStatus } from '../../config/metrics';
import DeviceToggle from '../ui/DeviceToggle';

const MONTH_NAMES = ['Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen', 'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec'];

const SortIcon = ({ direction }) => {
  if (!direction) return <Icons.ChevronDown size={12} className="text-slate-600" />;
  if (direction === 'asc') return <Icons.ChevronUp size={12} className="text-cyan-400" />;
  return <Icons.ChevronDown size={12} className="text-cyan-400" />;
};

export const MonthlyOverviewSection = memo(({ domain, selectedDevice: parentDevice, weeklyHistory }) => {
  const [localDevice, setLocalDevice] = useState(parentDevice || 'ALL');
  const [monthlyData, setMonthlyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState('weekly');
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ column: 'date', direction: 'desc' });
  
  const selectedDevice = localDevice;
  
  useEffect(() => {
    if (parentDevice && parentDevice !== localDevice) {
      setLocalDevice(parentDevice);
    }
  }, [parentDevice]);
  
  useEffect(() => {
    if (!domain) return;
    
    const fetchBigQueryMonthly = async () => {
      setLoading(true);
      try {
        const response = await fetch('/api/bigquery/core-vitals-monthly', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ origin: domain, formFactor: selectedDevice === 'COMPARE_ALL' ? 'ALL' : selectedDevice, monthsBack: 13 })
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.history) {
            const deviceKey = selectedDevice === 'PHONE' ? 'phone' : selectedDevice === 'DESKTOP' ? 'desktop' : 'all';
            const deviceData = data.history[deviceKey] || data.history['all'] || Object.values(data.history)[0];
            
            if (deviceData && deviceData.length > 0) {
              const parseYyyymm = (val) => {
                if (typeof val === 'number') return val;
                if (typeof val === 'string') {
                  if (val.includes('-')) {
                    const [y, m] = val.split('-');
                    return parseInt(y) * 100 + parseInt(m);
                  }
                  return parseInt(val.replace(/\D/g, ''));
                }
                return 0;
              };
              const sortedData = [...deviceData].sort((a, b) => parseYyyymm(b.yyyymm) - parseYyyymm(a.yyyymm));
              
              const now = new Date();
              const currentYyyymm = now.getFullYear() * 100 + (now.getMonth() + 1);
              const newestDataYyyymm = sortedData.length > 0 ? parseYyyymm(sortedData[0].yyyymm) : 0;
              const dataAgeMonths = Math.floor((currentYyyymm - newestDataYyyymm) / 100) * 12 + ((currentYyyymm % 100) - (newestDataYyyymm % 100));
              
              if (dataAgeMonths > 3) {
                console.log('BigQuery data is stale (', dataAgeMonths, 'months old), falling back to weekly');
                fallbackToWeekly();
                return;
              }
              
              setMonthlyData(sortedData.slice(0, 13));
              setDataSource('bigquery');
              setError(null);
              return;
            }
          }
        }
        fallbackToWeekly();
      } catch (err) {
        console.log('BigQuery monthly fetch failed, using weekly fallback:', err.message);
        fallbackToWeekly();
      } finally {
        setLoading(false);
      }
    };
    
    const fallbackToWeekly = () => {
      if (!weeklyHistory || weeklyHistory.length < 4) {
        setMonthlyData(null);
        return;
      }
      
      const byMonth = {};
      weeklyHistory.forEach(week => {
        const date = new Date(week.date);
        const yyyymm = date.getFullYear() * 100 + (date.getMonth() + 1);
        if (!byMonth[yyyymm] || new Date(week.date) > new Date(byMonth[yyyymm].date)) {
          byMonth[yyyymm] = {
            yyyymm,
            label: `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`,
            lcp_p75: week.metrics?.LCP?.p75,
            inp_p75: week.metrics?.INP?.p75,
            cls_p75: week.metrics?.CLS?.p75,
            fcp_p75: week.metrics?.FCP?.p75,
            ttfb_p75: week.metrics?.TTFB?.p75,
            date: week.date
          };
        }
      });
      
      const sorted = Object.values(byMonth).sort((a, b) => b.yyyymm - a.yyyymm);
      setMonthlyData(sorted.slice(0, 13));
      setDataSource('weekly');
    };
    
    fetchBigQueryMonthly();
  }, [domain, selectedDevice, weeklyHistory]);
  
  if (loading) {
    return (
      <div className="mt-8 p-4 bg-slate-800 rounded-lg flex items-center justify-center">
        <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mr-3" />
        <span className="text-slate-400 text-sm">Načítám měsíční data...</span>
      </div>
    );
  }
  
  if (!monthlyData || monthlyData.length < 2) return null;
  
  const formatDelta = (curr, prev, isCls = false) => {
    if (isNaN(curr) || prev === null || isNaN(prev)) return { text: '—', status: 'none' };
    const diff = curr - prev;
    const pct = Math.abs((diff / prev) * 100);
    if (pct < 5) return { text: '~', status: 'stable' };
    const text = isCls ? (diff >= 0 ? '+' : '') + diff.toFixed(3) : (diff >= 0 ? '+' : '') + Math.round(diff);
    return { text, status: diff < 0 ? 'better' : 'worse' };
  };
  
  const deltaColors = { better: 'text-green-400', worse: 'text-red-400', stable: 'text-slate-500', none: 'text-slate-500' };
  const statusColors = { 'good': 'text-green-400', 'needs-improvement': 'text-yellow-400', 'poor': 'text-red-400', 'unknown': 'text-slate-400' };
  
  const handleSort = (column) => {
    if (sortConfig.column === column) {
      if (sortConfig.direction === 'asc') setSortConfig({ column, direction: 'desc' });
      else if (sortConfig.direction === 'desc') setSortConfig({ column: 'date', direction: 'desc' });
      else setSortConfig({ column, direction: 'asc' });
    } else {
      setSortConfig({ column, direction: 'asc' });
    }
  };

  const parseYyyymm = (val) => {
    if (typeof val === 'number') return val;
    if (typeof val === 'string') {
      if (val.includes('-')) {
        const [y, m] = val.split('-');
        return parseInt(y) * 100 + parseInt(m);
      }
      return parseInt(val.replace(/\D/g, ''));
    }
    return 0;
  };

  const sortedData = [...monthlyData].sort((a, b) => {
    if (sortConfig.column === 'date') {
      const diff = parseYyyymm(b.yyyymm) - parseYyyymm(a.yyyymm);
      return sortConfig.direction === 'desc' ? diff : -diff;
    }
    
    const metricKey = {
      'LCP': 'lcp_p75',
      'INP': 'inp_p75', 
      'CLS': 'cls_p75'
    }[sortConfig.column];
    
    if (!metricKey) return 0;
    
    const aVal = parseFloat(a[metricKey]) || Infinity;
    const bVal = parseFloat(b[metricKey]) || Infinity;
    
    return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
  });
  
  const monthsToShow = sortedData.slice(0, 14);
  
  return (
    <div className="mt-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2 flex-wrap">
            <Icons.TrendingUp size={20} className="text-purple-400" />
            Měsíční přehled (MoM)
            <span className="text-xs font-normal bg-purple-500/20 text-purple-300 px-2 py-1 rounded">Posledních {monthsToShow.length} měsíců</span>
            {dataSource === 'bigquery' ? (
              <span className="text-xs font-normal bg-cyan-500/20 text-cyan-300 px-2 py-1 rounded flex items-center gap-1">
                <Icons.Database size={12} /> Měsíční
              </span>
            ) : (
              <span className="text-xs font-normal bg-slate-600/50 text-slate-400 px-2 py-1 rounded">Agregovaná týdenní</span>
            )}
          </h3>
          <p className="text-xs text-slate-400 mt-1">
            {dataSource === 'bigquery' 
              ? 'Skutečná měsíční data z Google CrUX. Změny zobrazují MoM delta oproti předchozímu měsíci.'
              : 'Agregovaná data - poslední týden každého měsíce. Změny zobrazují MoM delta oproti předchozímu měsíci.'}
          </p>
          {dataSource === 'bigquery' && (() => {
            const now = new Date();
            const currentMonth = now.getMonth();
            const currentYear = now.getFullYear();
            const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
            const prevYear = currentMonth === 0 ? currentYear - 1 : currentYear;
            const prevMonthYyyymm = prevYear * 100 + (prevMonth + 1);
            const newestYyyymm = monthlyData && monthlyData.length > 0 ? 
              (typeof monthlyData[0].yyyymm === 'number' ? monthlyData[0].yyyymm : parseInt(String(monthlyData[0].yyyymm).replace(/\D/g, ''))) : 0;
            const prevMonthName = MONTH_NAMES[prevMonth];
            
            if (newestYyyymm < prevMonthYyyymm) {
              return (
                <p className="text-xs text-amber-400/80 mt-1 flex items-center gap-1">
                  <Icons.Clock size={12} />
                  Data za {prevMonthName} {prevYear} budou dostupná přibližně 8.–15. {MONTH_NAMES[currentMonth].toLowerCase()} (Google publikuje měsíční data vždy 2. týden v měsíci).
                </p>
              );
            }
            return null;
          })()}
        </div>
        <DeviceToggle 
          value={localDevice} 
          onChange={setLocalDevice}
          showTablet={true}
        />
      </div>
      
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-900">
            <tr className="border-b border-slate-700">
              <th 
                className="text-left py-2 px-3 text-slate-400 cursor-pointer hover:text-white transition-colors select-none"
                onClick={() => handleSort('date')}
              >
                <span className="inline-flex items-center gap-1">
                  Měsíc
                  <SortIcon direction={sortConfig.column === 'date' ? sortConfig.direction : null} />
                </span>
              </th>
              <th 
                className="text-center py-2 px-3 text-slate-400 cursor-pointer hover:text-white transition-colors select-none"
                onClick={() => handleSort('LCP')}
              >
                <span className="inline-flex items-center justify-center gap-1">
                  LCP
                  <SortIcon direction={sortConfig.column === 'LCP' ? sortConfig.direction : null} />
                </span>
              </th>
              <th className="text-center py-2 px-3 text-slate-400">Δ</th>
              <th 
                className="text-center py-2 px-3 text-slate-400 cursor-pointer hover:text-white transition-colors select-none"
                onClick={() => handleSort('INP')}
              >
                <span className="inline-flex items-center justify-center gap-1">
                  INP
                  <SortIcon direction={sortConfig.column === 'INP' ? sortConfig.direction : null} />
                </span>
              </th>
              <th className="text-center py-2 px-3 text-slate-400">Δ</th>
              <th 
                className="text-center py-2 px-3 text-slate-400 cursor-pointer hover:text-white transition-colors select-none"
                onClick={() => handleSort('CLS')}
              >
                <span className="inline-flex items-center justify-center gap-1">
                  CLS
                  <SortIcon direction={sortConfig.column === 'CLS' ? sortConfig.direction : null} />
                </span>
              </th>
              <th className="text-center py-2 px-3 text-slate-400">Δ</th>
            </tr>
          </thead>
          <tbody>
            {monthsToShow.map((month, idx) => {
              const lcp = parseFloat(month.lcp_p75);
              const inp = parseFloat(month.inp_p75);
              const cls = parseFloat(month.cls_p75);
              const lcpStatus = getMetricStatus('LCP', lcp);
              const inpStatus = getMetricStatus('INP', inp);
              const clsStatus = getMetricStatus('CLS', cls);
              
              const isNewest = sortConfig.column === 'date' && sortConfig.direction === 'desc' && idx === 0;
              const isBestMetric = sortConfig.column !== 'date' && idx === 0;
              const isHighlighted = isNewest || isBestMetric;
              const showDelta = sortConfig.column === 'date';
              
              let lcpDelta = { text: '—', status: 'none' };
              let inpDelta = { text: '—', status: 'none' };
              let clsDelta = { text: '—', status: 'none' };
              
              if (showDelta) {
                const prevMonth = monthsToShow[idx + 1];
                const prevLcp = prevMonth ? parseFloat(prevMonth.lcp_p75) : null;
                const prevInp = prevMonth ? parseFloat(prevMonth.inp_p75) : null;
                const prevCls = prevMonth ? parseFloat(prevMonth.cls_p75) : null;
                lcpDelta = formatDelta(lcp, prevLcp);
                inpDelta = formatDelta(inp, prevInp);
                clsDelta = formatDelta(cls, prevCls, true);
              }
              
              return (
                <tr key={month.yyyymm || idx} className={`border-b border-slate-800 hover:bg-slate-800/50 ${isHighlighted ? (isBestMetric ? 'bg-green-500/10' : 'bg-cyan-500/10') : ''}`}>
                  <td className={`py-2 px-3 ${isHighlighted ? (isBestMetric ? 'text-green-300 font-semibold' : 'text-cyan-300 font-semibold') : 'text-slate-300'}`}>
                    {month.label}
                    {isNewest && <span className="ml-2 text-xs text-cyan-400">(aktuální)</span>}
                    {isBestMetric && <span className="ml-2 text-xs text-green-400">(nejlepší)</span>}
                  </td>
                  <td className="text-center py-2 px-3"><span className={`font-mono text-xs ${statusColors[lcpStatus]}`}>{!isNaN(lcp) ? (lcp / 1000).toFixed(2) + 's' : '—'}</span></td>
                  <td className="text-center py-2 px-3"><span className={`font-mono text-xs font-bold ${deltaColors[lcpDelta.status]}`}>{lcpDelta.text}</span></td>
                  <td className="text-center py-2 px-3"><span className={`font-mono text-xs ${statusColors[inpStatus]}`}>{!isNaN(inp) ? Math.round(inp) + 'ms' : '—'}</span></td>
                  <td className="text-center py-2 px-3"><span className={`font-mono text-xs font-bold ${deltaColors[inpDelta.status]}`}>{inpDelta.text}</span></td>
                  <td className="text-center py-2 px-3"><span className={`font-mono text-xs ${statusColors[clsStatus]}`}>{!isNaN(cls) ? cls.toFixed(3) : '—'}</span></td>
                  <td className="text-center py-2 px-3"><span className={`font-mono text-xs font-bold ${deltaColors[clsDelta.status]}`}>{clsDelta.text}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      
      <div className="mt-3 flex items-center gap-4 text-xs text-slate-500">
        <span className="flex items-center gap-1"><span className="text-green-400">↓</span> Zlepšení</span>
        <span className="flex items-center gap-1"><span className="text-red-400">↑</span> Zhoršení</span>
        <span className="flex items-center gap-1"><span className="text-slate-500">~</span> Stabilní (&lt;5%)</span>
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  return prevProps.domain === nextProps.domain && 
         prevProps.selectedDevice === nextProps.selectedDevice &&
         JSON.stringify(prevProps.weeklyHistory?.map(w => w.date)) === JSON.stringify(nextProps.weeklyHistory?.map(w => w.date));
});

MonthlyOverviewSection.displayName = 'MonthlyOverviewSection';

export default MonthlyOverviewSection;
